// src/Product/Accounting/TotalJournals.js
import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import "./TotalJournals.css";

const PAGE_SIZE = 10;

const TotalJournals = () => {
  const { companyId } = useCompany();

  const [journals, setJournals] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedJournal, setSelectedJournal] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTx, setLoadingTx] = useState(false);

  // ───────────────── Fetchers ─────────────────
  useEffect(() => {
    if (!companyId) return;
    fetchJournals();
    fetchChartOfAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, filterDate]);

  const fetchJournals = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("journal_entries")
        .select("journal_id, description, date, created_at")
        .eq("company_id", companyId)
        .order("date", { ascending: false });

      if (filterDate) query = query.eq("date", filterDate);

      const { data, error } = await query;
      if (error) throw error;

      // Group by journal_id (note: journal_id can be null for legacy rows)
      const map = (data || []).reduce((acc, row) => {
        const key = row.journal_id || "__NO_JOURNAL_ID__";
        acc[key] = acc[key] || row;
        return acc;
      }, {});

      const unique = Object.values(map);

      setJournals(unique);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error fetching journals:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartOfAccounts = async () => {
    const { data, error } = await supabase
      .from("chart_of_accounts")
      .select("id, name, type, account_number")
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    if (error) {
      console.error("Error fetching Chart of Accounts:", error);
    } else {
      setChartOfAccounts(data || []);
    }
  };

  const deleteJournal = async (journalId) => {
    if (!window.confirm("Delete this journal? This cannot be undone.")) return;

    // handle legacy null journal_id grouping key
    if (journalId === "__NO_JOURNAL_ID__") {
      alert(
        "This journal has no journal_id (legacy). Delete from the entries table directly if needed."
      );
      return;
    }

    const { error } = await supabase
      .from("journal_entries")
      .delete()
      .eq("journal_id", journalId)
      .eq("company_id", companyId);

    if (error) {
      console.error("Error deleting journal:", error);
      alert("Failed to delete journal.");
    } else {
      setJournals((prev) => prev.filter((j) => j.journal_id !== journalId));
    }
  };

  const fetchTransactions = async (journalId) => {
    setLoadingTx(true);

    let query = supabase
      .from("journal_entries")
      .select("*")
      .eq("company_id", companyId)
      .order("id", { ascending: true });

    // legacy: journal_id could be null
    if (journalId === "__NO_JOURNAL_ID__") {
      query = query.is("journal_id", null);
    } else {
      query = query.eq("journal_id", journalId);
    }

    const { data, error } = await query;

    setLoadingTx(false);
    if (error) {
      console.error("Error fetching transactions:", error);
    } else if (data?.length) {
      setTransactions(data);
      setSelectedJournal(journalId);
      setIsModalOpen(true);
    } else {
      setTransactions([]);
      setSelectedJournal(journalId);
      setIsModalOpen(true);
    }
  };

  // ───────────────── Edit logic ─────────────────
  // ✅ Use index-based updates so it works even if tx.id is null
  const updateTransactionByIndex = (idx, field, value) => {
    setTransactions((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t))
    );
  };

  const saveChanges = async () => {
    const totalIn = transactions.reduce(
      (sum, t) => sum + Number(t.money_in || 0),
      0
    );

    const totalOut = transactions.reduce(
      (sum, t) => sum + Number(t.money_out || 0),
      0
    );

    const roundedIn = Number(totalIn.toFixed(2));
    const roundedOut = Number(totalOut.toFixed(2));

    if (roundedIn !== roundedOut) {
      alert(
        `Money In and Money Out must balance. Money In=${roundedIn}, Money Out=${roundedOut}`
      );
      return;
    }

    if (!selectedJournal || selectedJournal === "__NO_JOURNAL_ID__") {
      alert(
        "This journal cannot be updated because it has no valid journal_id."
      );
      return;
    }

    try {
      const entries = transactions.map((tx) => ({
        date: tx.date,
        account_id: tx.account_id || null,
        description: tx.description || "",
        money_in: Number(Number(tx.money_in || 0).toFixed(2)),
        money_out: Number(Number(tx.money_out || 0).toFixed(2)),

        posting_status: tx.posting_status || "posted",
        tax_type: tx.tax_type || null,
        tax_base_amount: Number(tx.tax_base_amount || 0),
        tax_rate: Number(tx.tax_rate || 0),
        tax_amount: Number(tx.tax_amount || 0),
        taxable_flag: !!tx.taxable_flag,
        recoverable_flag: !!tx.recoverable_flag,
        exempt_flag: !!tx.exempt_flag,
        zero_rated_flag: !!tx.zero_rated_flag,
        withholding_flag: !!tx.withholding_flag,
        deductible_flag:
          tx.deductible_flag === undefined ? true : !!tx.deductible_flag,

        counterparty_id: tx.counterparty_id || null,
      }));

      const { error } = await supabase.rpc("fn_replace_journal_entries", {
        p_company_id: companyId,
        p_journal_id: selectedJournal,
        p_entries: entries,
      });

      if (error) throw error;

      alert("Journal updated successfully!");
      setIsModalOpen(false);
      fetchJournals();
    } catch (err) {
      console.error("Error saving journal:", err);
      alert(err?.message || "Failed to save changes.");
    }
  };

  // ───────────────── Derived UI state ─────────────────
  const pageCount = Math.max(1, Math.ceil(journals.length / PAGE_SIZE));
  const paged = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return journals.slice(start, start + PAGE_SIZE);
  }, [journals, currentPage]);

  const totalIn = useMemo(
    () => transactions.reduce((sum, t) => sum + parseFloat(t.money_in || 0), 0),
    [transactions]
  );
  const totalOut = useMemo(
    () =>
      transactions.reduce((sum, t) => sum + parseFloat(t.money_out || 0), 0),
    [transactions]
  );
  const balanced = totalIn === totalOut;

  // ───────────────── Render ─────────────────
  return (
    <div className="journals-page">
      <header className="journals-hero">
        <div>
          <p className="journals-eyebrow">Accounting Records</p>
          <h1>Journals</h1>
          <p>
            View, filter, edit and delete journal entries. Every journal must
            remain balanced before changes are saved.
          </p>
        </div>

        <div className="journal-date-filter">
          <label htmlFor="filter-date">Filter by Date</label>
          <input
            id="filter-date"
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
          />
        </div>
      </header>

      <section className="journals-panel">
        <div className="journals-panel-head">
          <div>
            <h3>Journal Entries</h3>
            <p>Showing posted accounting entries for the selected period.</p>
          </div>
        </div>

        {loading ? (
          <div className="skeleton-list">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton-row" />
            ))}
          </div>
        ) : journals.length === 0 ? (
          <div className="journals-empty">
            <div className="journals-empty-icon">📘</div>
            <h3>No journals found</h3>
            <p>Create journals from the Journal form to see them here.</p>
          </div>
        ) : (
          <>
            <div className="journals-list">
              {paged.map((journal) => (
                <div
                  key={journal.journal_id || "__NO_JOURNAL_ID__"}
                  className="journal-card"
                >
                  <div className="journal-card-top">
                    <div>
                      <span>Date</span>
                      <strong>
                        {journal.date
                          ? new Date(journal.date).toLocaleDateString()
                          : "—"}
                      </strong>
                    </div>

                    <div>
                      <span>Journal ID</span>
                      <strong className="mono">
                        {journal.journal_id || "Legacy entry"}
                      </strong>
                    </div>
                  </div>

                  <p className="journal-desc">
                    {journal.description || <em>No description</em>}
                  </p>

                  <div className="journal-actions">
                    <button
                      className="journal-btn ghost"
                      onClick={() =>
                        fetchTransactions(
                          journal.journal_id || "__NO_JOURNAL_ID__"
                        )
                      }
                    >
                      Edit
                    </button>

                    <button
                      className="journal-btn danger"
                      onClick={() =>
                        deleteJournal(journal.journal_id || "__NO_JOURNAL_ID__")
                      }
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="journal-pagination">
              <button
                className="journal-btn ghost"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                ← Prev
              </button>

              <span>
                Page <strong>{currentPage}</strong> of{" "}
                <strong>{pageCount}</strong>
              </span>

              <button
                className="journal-btn ghost"
                disabled={currentPage === pageCount}
                onClick={() =>
                  setCurrentPage((p) => Math.min(pageCount, p + 1))
                }
              >
                Next →
              </button>
            </div>
          </>
        )}
      </section>

      {isModalOpen && (
        <div
          className="journal-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="journal-modal">
            <div className="journal-modal-head">
              <div>
                <p className="journals-eyebrow">Journal Editor</p>
                <h3>Edit Journal</h3>
                <p className="mono">
                  {selectedJournal === "__NO_JOURNAL_ID__"
                    ? "Legacy entries without journal_id"
                    : selectedJournal}
                </p>
              </div>

              <button
                className="journal-modal-close"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>

            {loadingTx ? (
              <div className="skeleton-table" />
            ) : (
              <>
                <div className="journal-balance-banner">
                  <div className={`journal-chip ${balanced ? "ok" : "warn"}`}>
                    {balanced ? "Balanced" : "Unbalanced"}
                  </div>

                  <div className="journal-totals">
                    <span>
                      Money In <strong>₦{totalIn.toLocaleString()}</strong>
                    </span>
                    <span>
                      Money Out <strong>₦{totalOut.toLocaleString()}</strong>
                    </span>
                  </div>
                </div>

                <div className="journal-edit-table-wrap">
                  <div className="journal-edit-table">
                    <div className="journal-edit-head">
                      <span>Date</span>
                      <span>Account</span>
                      <span>Description</span>
                      <span>Money In</span>
                      <span>Money Out</span>
                    </div>

                    {transactions.map((t, idx) => (
                      <div
                        key={t.id || `${selectedJournal}-${idx}`}
                        className="journal-edit-row"
                      >
                        <input
                          type="date"
                          value={t.date || ""}
                          onChange={(e) =>
                            updateTransactionByIndex(
                              idx,
                              "date",
                              e.target.value
                            )
                          }
                        />

                        <select
                          value={t.account_id || ""}
                          onChange={(e) =>
                            updateTransactionByIndex(
                              idx,
                              "account_id",
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select Account</option>
                          {chartOfAccounts.map((acct) => (
                            <option key={acct.id} value={acct.id}>
                              {acct.account_number
                                ? `${acct.account_number} - `
                                : ""}
                              {acct.name} ({acct.type})
                            </option>
                          ))}
                        </select>

                        <input
                          type="text"
                          value={t.description || ""}
                          placeholder="Narration"
                          onChange={(e) =>
                            updateTransactionByIndex(
                              idx,
                              "description",
                              e.target.value
                            )
                          }
                        />

                        <input
                          type="number"
                          inputMode="decimal"
                          value={t.money_in || 0}
                          onChange={(e) =>
                            updateTransactionByIndex(
                              idx,
                              "money_in",
                              parseFloat(e.target.value) || 0
                            )
                          }
                        />

                        <input
                          type="number"
                          inputMode="decimal"
                          value={t.money_out || 0}
                          onChange={(e) =>
                            updateTransactionByIndex(
                              idx,
                              "money_out",
                              parseFloat(e.target.value) || 0
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="journal-modal-actions">
                  <button className="journal-btn primary" onClick={saveChanges}>
                    Save Changes
                  </button>
                  <button
                    className="journal-btn ghost"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TotalJournals;
